<template>
  <f7-popup
    v-model:opened="isPopupOpened"
    class="main-pc-part-history-popup"
    close-on-escape
  >
    <!-- Content of the popup -->
    <f7-page v-if="isPopupOpened">
      <template #fixed>
        <TheNavbar
          nav-title="Histórico de partes"
          class="secondary-navbar"
          :popup-close-btn-enable="true"
        />
      </template>

      <UCPreload
        :show="isLoadingHistory"
        :number="3"
      />

      <Transition>
        <f7-block v-if="!isLoadingHistory && workPart">
          <h3>{{ `${$t('common.article')}: ${formattedWorkPart}` }}</h3>

          <f7-block>
            Listado de partes
          </f7-block>
          <f7-list accordion-list>
            <f7-list-item
              v-for="(part, i) in machineHistory"
              :key="i"
              accordion-item
              :title="getPartTitle(part)"
              link="#"
              @accordion:open="onConsumptionsAccordionOpen(part.idParte)"
            >
              <f7-accordion-content>
                <UCPreload
                  :show="isLoadingConsumptions[part.idParte]"
                  :number="1"
                />

                <Transition>
                  <f7-block
                    v-if="!isLoadingConsumptions[part.idParte]"
                    class="container-table"
                  >
                    <table>
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            class="text-align-center"
                          >
                            {{ $t('common.warehouse') }}
                          </th>
                          <th
                            scope="col"
                            class="text-align-center"
                          >
                            {{ $t('common.article') }}
                          </th>
                          <th
                            scope="col"
                            class="text-align-center"
                          >
                            {{ $t('common.name') }}
                          </th>
                          <th
                            scope="col"
                            class="text-align-center"
                          >
                            {{ $t('common.quantity') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-if="consumptions.length > 0">
                          <tr
                            v-for="(item, j) in consumptions"
                            :key="j"
                          >
                            <td class="text-align-center">
                              {{ item.Almacen?.Nombre || '—' }}
                            </td>
                            <td class="text-align-center">
                              {{ item.Articulo?.Codigo || '—' }}
                            </td>
                            <td class="text-align-center">
                              {{ getFormattedItemName(item) }}
                            </td>
                            <td class="text-align-center">
                              {{ item.Cantidad }}
                            </td>
                          </tr>
                        </template>
                        <!-- No data to show -->
                        <template v-else>
                          <tr>
                            <td
                              colspan="4"
                              class="text-align-center"
                            >
                              No hay datos
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </f7-block>
                </Transition>
              </f7-accordion-content>
            </f7-list-item>
          </f7-list>
        </f7-block>
      </Transition>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check

import { computed, ref } from 'vue';
import { HiDateTimeSupport } from '@/js/common/HIConversionSupport';
import { HiNotification } from '@/js/common/HINotificationSupport';
import { callFetchMachineHistory, callFetchRealConsumptions } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import TheNavbar from '@/components/TheNavbar.vue';
import UCPreload from '@/components/common/UCPreload.vue';

/** @import { Ref } from 'vue' */
/** @import { WorkPartDetails } from '@/js/stores/repairParts' */
/** @import { ParteProduccionRealEntrada } from '@erpagroapp/types' */

/** Stores if the popup should be displayed. */
const isPopupOpened = ref(false);
/** @type {Ref<WorkPartDetails | null>} */
const workPart = ref(null);

/**
 * Stores a list of the history of the machine.
 * @type {Ref<Record<string, any>[]>}
 */
const machineHistory = ref([]);

/**
 * Stores a list of consumptions of the work part.
 * @type {Ref<ParteProduccionRealEntrada[]>}
 */
const consumptions = ref([]);

/**
 * Cache object to store consumptions for each work part.
 * @type {Ref<Record<number, ParteProduccionRealEntrada[]>>}
 */
const consumptionsCache = ref({});

/** Whether the part history is loading. */
const isLoadingHistory = ref(false);

/**
 * Cache whether the consumptions are loading for each work part.
 * @type {Ref<Record<number, boolean>>}
 */
const isLoadingConsumptions = ref({});

const formattedWorkPart = computed(() => (workPart.value
  ? `${workPart.value.NombreArticulo} (${workPart.value.NumeroSerie})`
  : ''
));

async function fetchMachineHistory() {
  if (!workPart.value) {
    return;
  }

  try {
    isLoadingHistory.value = true;
    const { data } = await callFetchMachineHistory(
      workPart.value.Id_Articulo,
      workPart.value.NumeroSerie,
    );
    isLoadingHistory.value = false;

    if (Array.isArray(data)) {
      machineHistory.value = data;
    } else {
      HiNotification.Toast('No se ha podido obtener el histórico de partes');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido obtener el histórico de partes');
  } finally {
    isLoadingHistory.value = false;
  }
}

function closePopup() {
  isPopupOpened.value = false;
}

/**
 * A method that is called from main page to open the popup
 * @param {WorkPartDetails} details - The work part.
 */
async function openPopup(details) {
  if (!details) {
    throw new Error('No work part provided');
  }

  workPart.value = details;

  try {
    isPopupOpened.value = true;
    await fetchMachineHistory();
  } finally {
    if (machineHistory.value.length === 0) {
      closePopup();
    }
  }
}

/**
 * Fetches expected consumption articles.
 *
 * @param {number} workPartId - The ID of the work part.
 * @returns {Promise<void>}
 */
async function fetchRealConsumptions(workPartId) {
  try {
    isLoadingConsumptions.value[workPartId] = true;
    const { data } = await callFetchRealConsumptions(workPartId);
    isLoadingConsumptions.value[workPartId] = false;

    if (Array.isArray(data)) {
      consumptions.value = data;
      // Cache the consumptions for this work part
      consumptionsCache.value[workPartId] = data;
    } else {
      HiNotification.Toast('No se ha podidon cargar las consumiciones reales');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podidon cargar las consumiciones reales');
  } finally {
    isLoadingConsumptions.value[workPartId] = false;
  }
}

/**
 * Handles the opening of the consumptions accordion.
 * @param {number} workPartId - The ID of the work part
 */
async function onConsumptionsAccordionOpen(workPartId) {
  if (!consumptionsCache.value[workPartId]) {
    consumptions.value = [];
    // If not, fetch the real consumptions and store them in the cache
    await fetchRealConsumptions(workPartId);
  } else {
    // Update consumptions with the cached data
    consumptions.value = consumptionsCache.value[workPartId] || [];
  }
}

/**
 * Formats date to the `DD/MM/YYYY` format.
 * @param {Date} date
 */
function formatDate(date) {
  return HiDateTimeSupport.FormatDateTime(date, 'DD/MM/YYYY');
}

/**
 * Returns the title of the part.
 * @param {Record<string, any>} part
 */
function getPartTitle(part) {
  return `${part.serieParte} (${formatDate(part.fechaParte)}) - ${part.observacionesOrden}`;
}

/**
 * Returns the article name with observations.
 * @param {ParteProduccionRealEntrada} item - The article item.
 */
function getFormattedItemName(item) {
  const itemName = item.Articulo?.Nombre || '';
  const observations = item.Observaciones;

  if (observations) {
    return `${itemName}<br>(${observations})`;
  }

  return itemName;
}

defineExpose({
  openPopup,
  closePopup,
});
</script>

<style scoped lang="scss">
.main-pc-part-history-popup {
  .container-table {
    overflow-x: auto;

    table {
      width: 100%;
      border-collapse: collapse;
      border-spacing: 0;

      thead {
        background-color: #9e9e9e;

        th {
          background-color: #9e9e9e;
          padding: 5px;
        }

        th:first-child {
          padding-left: calc(var(--f7-typography-padding) / 2);
          border-top-left-radius: 5px;
        }

        th:last-child {
          padding-right: calc(var(--f7-typography-padding) / 2);
          border-top-right-radius: 5px;
        }
      }

      tbody {
        background-color: #e6e6e6;

        td {
          padding: 5px;
        }

        td:first-child {
          padding-left: calc(var(--f7-typography-padding) / 2);
        }

        td:last-child {
          padding-right: calc(var(--f7-typography-padding) / 2);
        }
      }
    }
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}
</style>
